import { useEffect, useRef, useState } from 'react';
import { alpha, Box, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ChatIcon from '@mui/icons-material/Chat';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { getCrmColors, inkOver } from '../../kanban-board/crm-theme';
import {
    ELeadOutcome, LeadResponse, LeadStageResponse,
} from '../../kanban-board/services/responses/lead.response';
import { LeadStageBar } from './lead-stage-bar';
import { LeadTags } from './lead-tags';
import { formatPhone } from '@functions/format-phone';
import { leadChannel, readableChannelColor } from '../lead-channel';
import { useLeadStatus } from '../../kanban-board/hooks/use-lead-status.hook';
import { CrmLeadService } from '../../kanban-board/services/crm-lead.service';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { AiAssistant } from '@components/AiAssistant';
import { useLeadAttachmentUpload } from '../hooks/use-lead-attachment-upload.hook';
import { LeadPaymentService, PaymentProvider } from '../services/lead-payment.service';
import { LeadPaymentModal } from './lead-payment-modal';
import { TeamEmailService } from '../services/team-email.service';
import { useProfilePermissions } from '@hooks/use-profile-permissions.hook';

interface Props {
    lead: LeadResponse;
    company?: { id: string; name: string } | null;
    onWin: () => void;
    onLose: () => void;
    onReopen: () => void;
    onChanged: () => void;
    stages: LeadStageResponse[];
    onMove: (stageId: string) => void;
}

const initials = (name: string) =>
    name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('');

export const LeadHeader = ({
    lead, company, onWin, onLose, onReopen, onChanged, stages, onMove,
}: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const navigate = useNavigate();
    const { statuses } = useLeadStatus();
    const { showAlert } = useAlert();

    const [anchor, setAnchor] = useState<null | HTMLElement>(null);
    const [providers, setProviders] = useState<PaymentProvider[]>([]);
    const [charging, setCharging] = useState(false);
    const [crmAddress, setCrmAddress] = useState<string | null>(null);
    const { canCloseLeads } = useProfilePermissions();

    useEffect(() => {
        new LeadPaymentService()
            .providers()
            .then((res) => setProviders(res.data.data ?? []))
            .catch(() => setProviders([]));

        new TeamEmailService()
            .address()
            .then((res) => setCrmAddress(res.data.data?.address ?? null))
            .catch(() => setCrmAddress(null));
    }, []);

    const changeStatus = async (statusId: string) => {
        setAnchor(null);

        if (statusId === lead.statusId) return;

        try {
            await new CrmLeadService().changeStatus(lead.id, statusId);
            onChanged();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        }
    };

    const closed = lead.outcome !== ELeadOutcome.Open;

    // O botão fala o canal de chegada; quando a origem não é canal (manual, anúncio), fica genérico.
    const channel = leadChannel(lead.source);
    const ChannelIcon = channel?.Icon ?? ChatIcon;

    // Cor da paleta da marca; a do TikTok é quase preta e sumiria no escuro, então usa a do tema.
    const channelColor = (() => {
        if (!channel) return cc.accent;

        return readableChannelColor(channel.color, cc.cardBg, cc.textPrimary);
    })();

    const buttonSx = (color: string) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        height: 36,
        px: 1.9,
        borderRadius: '9px',
        fontSize: 12.5,
        fontWeight: 700,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        color: cc.onTint,
        border: `1px solid ${color}66`,
        bgcolor: `${color}22`,
        userSelect: 'none',
        '& .MuiSvgIcon-root': { color },
    });

    const fileInput = useRef<HTMLInputElement>(null);
    const { send, sending, enabled: uploadEnabled, warnUnavailable } = useLeadAttachmentUpload(lead.id, onChanged);

    const pickFiles = () => {
        if (!uploadEnabled) return warnUnavailable();
        if (!sending) fileInput.current?.click();
    };

    /// Volta para de onde veio; aberto por link, sem tela anterior, vai ao quadro.
    const goBack = () => {
        if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
        else navigate('/crm/quadro-kanban');
    };

    return (
        <Box>
            {/* Voltar e etapas numa linha; laterais iguais deixam as etapas no centro da página. */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 2, mb: 1.75 }}>
                <Box
                    role="button"
                    tabIndex={0}
                    onClick={goBack}
                    onKeyDown={(e) => e.key === 'Enter' && goBack()}
                    // Com fundo e borda de botão: solto no canto, o texto lia como legenda e sumia.
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifySelf: 'start',
                        gap: 0.75,
                        height: 32,
                        px: 1.5,
                        color: cc.textPrimary,
                        fontSize: 13,
                        fontWeight: 700,
                        bgcolor: cc.countBg,
                        border: `1px solid ${cc.borderStrong}`,
                        borderRadius: '9px',
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': { borderColor: cc.borderHover },
                    }}
                >
                    <ArrowBackIcon sx={{ fontSize: 17 }} />
                    {t('telaLead.voltar')}
                </Box>

                {/* Um pouco à esquerda do centro: a ponta da última seta pesa para a direita. */}
                <Box sx={{ display: 'flex', minWidth: 0, overflowX: 'auto', transform: 'translateX(-32px)' }}>
                    <LeadStageBar stages={stages} currentId={lead.columnId} onMove={onMove} />
                </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, flexWrap: 'wrap' }}>
                <Box
                    sx={{
                        width: 46,
                        height: 46,
                        borderRadius: '50%',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 15,
                        bgcolor: lead.statusColor || cc.accent,
                        color: inkOver(lead.statusColor || cc.accent),
                    }}
                >
                    {initials(lead.contactName)}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{ fontSize: 21, fontWeight: 700, color: cc.textPrimary, lineHeight: 1.2 }}
                    >
                        {lead.contactName}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 0.5, flexWrap: 'wrap' }}>
                        {lead.contactPhone && (
                            <Typography variant="caption" sx={{ color: cc.textSecondary }}>
                                {formatPhone(lead.contactPhone)}
                            </Typography>
                        )}

                        {/* Com o e-mail da equipe ligado, o link põe o CRM em cópia e a conversa entra no histórico. */}
                        {lead.contactEmail && (
                            <Tooltip title={crmAddress ? t('telaLead.emailComCopia') : ''}>
                                <Typography
                                    variant="caption"
                                    component="a"
                                    href={`mailto:${lead.contactEmail}${crmAddress ? `?cc=${encodeURIComponent(crmAddress)}` : ''}`}
                                    sx={{
                                        color: cc.textSecondary,
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' },
                                    }}
                                >
                                    {lead.contactEmail}
                                </Typography>
                            </Tooltip>
                        )}

                        {/* Só leitura: a empresa se troca no cadastro da pessoa. */}
                        {company && (
                            <Tooltip title={t('companies.abrirEmpresa')}>
                                <Typography
                                    variant="caption"
                                    component="a"
                                    href={`/crm/empresas?empresa=${company.id}`}
                                    onClick={(e: React.MouseEvent) => {
                                        e.preventDefault();
                                        navigate(`/crm/empresas?empresa=${company.id}`);
                                    }}
                                    sx={{
                                        color: cc.textSecondary,
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' },
                                    }}
                                >
                                    {company.name}
                                </Typography>
                            </Tooltip>
                        )}

                        {/* Clicável: trocar a temperatura é o mais frequente e exigia voltar ao quadro. */}
                        <Tooltip title={t('telaLead.trocarStatus')}>
                            <Box
                                role="button"
                                tabIndex={0}
                                onClick={(e) => setAnchor(e.currentTarget)}
                                onKeyDown={(e) => e.key === 'Enter' && setAnchor(e.currentTarget)}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.6,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    borderRadius: '999px',
                                    px: 1.25,
                                    py: 0.35,
                                    cursor: 'pointer',
                                    bgcolor: `${lead.statusColor}2e`,
                                    color: cc.onTint,
                                }}
                            >
                                <Box component="span" sx={{ color: lead.statusColor }}>●</Box>
                                {lead.statusName}
                                <ExpandMoreIcon sx={{ fontSize: 14, color: lead.statusColor }} />
                            </Box>
                        </Tooltip>

                        {/* Mesmo desenho do menu do card: a mesma escolha não pode ter duas caras. */}
                        <Menu
                            anchorEl={anchor}
                            open={Boolean(anchor)}
                            onClose={() => setAnchor(null)}
                            PaperProps={{
                                sx: {
                                    bgcolor: cc.menuBg,
                                    backgroundImage: `linear-gradient(${alpha(lead.statusColor, 0.18)}, ${alpha(lead.statusColor, 0.18)})`,
                                    border: `1px solid ${alpha(lead.statusColor, 0.55)}`,
                                },
                            }}
                        >
                            {statuses.map((status) => (
                                <MenuItem
                                    key={status.id}
                                    selected={status.id === lead.statusId}
                                    onClick={() => changeStatus(status.id)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        color: cc.textPrimary,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            bgcolor: status.color,
                                        }}
                                    />
                                    <Typography variant="caption" fontWeight={700}>
                                        {status.name}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Menu>

                        {lead.assignedUserName && (
                            <Box
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    borderRadius: '999px',
                                    px: 1.25,
                                    py: 0.35,
                                    bgcolor: cc.countBg,
                                    color: cc.textSecondary,
                                }}
                            >
                                {lead.assignedUserName}
                            </Box>
                        )}

                    </Box>

                    {/* Linha própria: junto do telefone, cada tag alargava o bloco do nome. */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 1 }}>
                        <LeadTags lead={lead} onChanged={onChanged} />
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                        ml: 'auto',
                    }}
                >
                    {/* Primeiro da fila: é ação sobre o lead e o contorno já o destaca. */}
                    <AiAssistant
                        conversationId={lead.chatwootConversationId}
                        inboxId={lead.chatwootInboxId}
                        leadId={lead.id}
                        onApplied={onChanged}
                        fullWidth={false}
                    />

                    {/* Ferramentas antes, desfecho no fim; com texto, pois só o ícone lia como botão desligado. */}
                    <input
                        ref={fileInput}
                        type="file"
                        hidden
                        multiple
                        onChange={(e) => {
                            send(e.target.files);
                            e.target.value = '';
                        }}
                    />
                    <Tooltip title={t('telaLead.anexarArquivo')}>
                        <Box
                            role="button"
                            tabIndex={0}
                            onClick={pickFiles}
                            onKeyDown={(e) => e.key === 'Enter' && pickFiles()}
                            sx={{ ...buttonSx(cc.accent), opacity: sending ? 0.6 : 1 }}
                        >
                            <AttachFileIcon sx={{ fontSize: 16 }} />
                            {t('telaLead.anexar')}
                        </Box>
                    </Tooltip>

                    {providers.length > 0 && !closed && (
                        <Tooltip title={t('cobranca.ajuda')}>
                            <Box
                                role="button"
                                tabIndex={0}
                                onClick={() => setCharging(true)}
                                onKeyDown={(e) => e.key === 'Enter' && setCharging(true)}
                                sx={buttonSx(cc.accent)}
                            >
                                <PaymentsOutlinedIcon sx={{ fontSize: 16 }} />
                                {t('cobranca.cobrar')}
                            </Box>
                        </Tooltip>
                    )}

                    {/* Leva ao Multicanal em vez de duplicar a conversa aqui. */}
                    {lead.chatwootConversationId && (
                        <Box
                            role="button"
                            tabIndex={0}
                            onClick={() =>
                                navigate(`/multicanal?conversa=${lead.chatwootConversationId}`)}
                            onKeyDown={(e) => e.key === 'Enter'
                                && navigate(`/multicanal?conversa=${lead.chatwootConversationId}`)}
                            sx={buttonSx(channelColor)}
                        >
                            <ChannelIcon sx={{ fontSize: 16 }} />
                            {channel
                                ? t('telaLead.abrirNoCanal', { canal: channel.label })
                                : t('telaLead.abrirConversa')}
                        </Box>
                    )}

                    {canCloseLeads && (closed ? (
                        <Tooltip title={t('telaLead.reabrirAjuda')}>
                            <Box role="button" tabIndex={0} onClick={onReopen} sx={buttonSx(cc.textSecondary)}>
                                <ReplayIcon sx={{ fontSize: 16 }} />
                                {t('telaLead.reabrir')}
                            </Box>
                        </Tooltip>
                    ) : (
                        <>
                            <Box role="button" tabIndex={0} onClick={onLose} sx={buttonSx(cc.eventDanger)}>
                                <CloseIcon sx={{ fontSize: 16 }} />
                                {t('desfecho.perdido')}
                            </Box>
                            <Box role="button" tabIndex={0} onClick={onWin} sx={buttonSx(cc.eventSuccess)}>
                                <CheckIcon sx={{ fontSize: 16 }} />
                                {t('desfecho.ganho')}
                            </Box>
                        </>
                    ))}
                </Box>
            </Box>

            <LeadPaymentModal
                lead={lead}
                providers={providers}
                open={charging}
                onClose={() => setCharging(false)}
                onCreated={onChanged}
            />
        </Box>
    );
};
